import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle, RotateCcw, Trash2 } from 'lucide-react'

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

/**
 * App-level error boundary.
 *
 * Catches uncaught errors from any descendant render, lifecycle, or
 * constructor and renders a recoverable fallback UI instead of the
 * blank/white screen React shows by default. Two things make this load-
 * bearing for DebtOS:
 *
 * 1. **Phone users can't open DevTools.** When something crashes on
 *    mobile (a malformed localStorage shape after a JSON restore, an
 *    auth init error, a stale bundle interacting with a new schema),
 *    the only "fix" without DevTools is to clear site data through the
 *    browser's deeply-nested settings menus. That's a real lockout.
 *    The fallback UI here exposes a "Reset local data" button that
 *    nukes the `debtos:*` localStorage namespace in one tap, the
 *    equivalent of the DevTools recovery snippet but reachable from
 *    inside the app.
 *
 * 2. **The "blank blue screen" failure mode.** Without this boundary,
 *    a render-time error would leave the page showing only the
 *    `bg-slate-950` body fill — the same dark navy as our loading
 *    placeholder — which is indistinguishable from a hung load.
 *    With the boundary, the user sees an explicit error state and
 *    knows the app crashed (vs. is still loading).
 *
 * Error info is logged to the console for inspection. We don't ship
 * the boundary off to Sentry/etc. yet — privacy posture is local-first
 * and we haven't added any error-reporting service.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[debtos] Error boundary caught:', error, info)
  }

  private handleReload = (): void => {
    window.location.reload()
  }

  private handleResetLocalData = (): void => {
    try {
      const keys = Object.keys(localStorage).filter((k) => k.startsWith('debtos:'))
      for (const k of keys) {
        localStorage.removeItem(k)
      }
    } catch {
      // Storage unavailable — fall through to reload anyway.
    }
    window.location.reload()
  }

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children

    return (
      <div className="min-h-dvh bg-slate-950 text-white flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-slate-900 border border-red-500/30 rounded-xl p-6 shadow-2xl">
          <div className="flex items-start gap-3 mb-4">
            <div className="shrink-0 w-10 h-10 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center justify-center text-red-400">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-white">Something went wrong</h1>
              <p className="text-xs text-slate-400 mt-0.5">
                DebtOS crashed while loading. This usually means localStorage has data the new
                bundle doesn&rsquo;t recognise — typically after a restore from an older backup.
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <button
              onClick={this.handleReload}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-slate-700 hover:border-slate-600 hover:bg-slate-800 text-slate-200 rounded-lg text-sm font-medium transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              Try reloading
            </button>
            <button
              onClick={this.handleResetLocalData}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-500 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Reset local data &amp; reload
            </button>
          </div>

          <p className="text-[11px] text-slate-500 mt-4 leading-relaxed">
            <span className="text-slate-400 font-semibold">Reset local data</span> clears DebtOS
            keys in this browser&rsquo;s localStorage. Your cloud data (if signed in) is untouched —
            the app will refetch on next load.
          </p>

          {this.state.error ? (
            <details className="mt-4">
              <summary className="text-[11px] text-slate-500 cursor-pointer hover:text-slate-300">
                Technical details
              </summary>
              <pre className="mt-2 p-2 bg-slate-950/60 border border-slate-800 rounded text-[10px] text-slate-400 overflow-x-auto whitespace-pre-wrap break-words">
                {this.state.error.name}: {this.state.error.message}
                {this.state.error.stack ? `\n\n${this.state.error.stack}` : ''}
              </pre>
            </details>
          ) : null}
        </div>
      </div>
    )
  }
}
